import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import {
  Action,
  State,
} from "~/components/search-page-wrapper/infinite-scroll/stateMachine";
import styled from "@emotion/styled";
import { FilterForm, FilterOptions } from "./FilterForm";
import { VisibilityListener } from "~/visibility-listener";
import { SearchFilterContainer } from "~/components/search-filter-container";
import useInfiniteScroll from "~/components/search-page-wrapper/infinite-scroll/useInfiniteScroll";
import { ResetSearchItemsProvider } from "~/components/search-page-wrapper/infinite-scroll/ResetSearchItems";
import { TextHighlighterProvider } from "~/components/text-highlighter/TextHighlighterProvider";
import { RoleTitleRow } from "~/roles/role-title-row/RoleTitleRow";

import { useRolesSearchQuery } from "./query.generated";
import { useRemoveRole } from "./useRemoveRole";
import {
  SearchPageWrapper,
  SearchListContent,
} from "~/components/search-page-wrapper";
import { RoleAssignment } from "./types";
import { EmployeeRolePane } from "./EmployeeRolePane";

const ContentArea = styled("div")`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 16px;
  overflow: hidden;
  padding-top: 16px;
  width: 100%;
  & .search-list-content {
    margin-top: 0px;
    padding-bottom: 0px;
  }
`;

const LeftContentPane = styled("div")`
  overflow: hidden;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

export type RolesSearchDisplayProps = {
  showSpinner: boolean;
  roles: RoleAssignment[] | null;
  state: State<RoleAssignment, FilterOptions>;
  dispatch: React.Dispatch<Action<RoleAssignment, FilterOptions>>;
};

export const RoleSearchPageDisplay: React.FC<RolesSearchDisplayProps> = (
  props
) => {
  const [roleAssignment, setRoleAssignment] = useState<RoleAssignment | null>(
    null
  );

  const { showSpinner, roles, state, dispatch } = props;

  const { removeRole, loading } = useRemoveRole();

  useEffect(() => {
    setRoleAssignment(null);
  }, [roles]);

  return (
    <ContentArea>
      <SearchListContent
        loading={showSpinner || loading}
        loadingMore={state.tag === "LoadingMore"}
      >
        <LeftContentPane>
          {roles &&
            roles.map((role, idx) => (
              <RoleTitleRow
                key={idx}
                role={role}
                setRoleAssignment={setRoleAssignment}
                selected={role === roleAssignment}
              />
            ))}
        </LeftContentPane>

        {state.showVisibility && (
          <VisibilityListener
            onShown={() => dispatch({ tag: "ScrolledToBottom" })}
          />
        )}
      </SearchListContent>

      <EmployeeRolePane selectedRole={roleAssignment} removeRole={removeRole} />
    </ContentArea>
  );
};

type LoadRolesProps = {
  dispatch: React.Dispatch<Action<RoleAssignment, FilterOptions>>;
  tag: "DataLoaded" | "MoreDataLoaded";
  state: State<RoleAssignment, FilterOptions>;
};

const LoadRoles = ({ dispatch, tag, state }: LoadRolesProps) => {
  const token = state.searchToken;

  const { searchText, role } = state.filterOptions;

  const variables = {
    searchText: searchText || null,
    roleName: role || null,
    token: token || null,
  };

  const { data } = useRolesSearchQuery({ variables });

  useEffect(() => {
    if (data) {
      const newToken = data?.employees?.roleSearch?.token as string | null;
      const newRoles = data?.employees?.roleSearch?.records as RoleAssignment[];
      dispatch({ tag, items: newRoles, searchToken: newToken });
    }
  }, [data]);

  return <></>;
};

type RolesPageProps = {
  FilterFormComponent?: typeof FilterForm;
};

export const RolesPage: React.FC<RolesPageProps> = (props) => {
  const [filters, setFilters] = useState<FilterOptions | null>(null);
  const FilterFormComponent = props.FilterFormComponent || FilterForm;

  const setFilterOptions = (opts: FilterOptions) => setFilters(opts);

  return (
    <SearchPageWrapper>
      <SearchFilterContainer>
        <FilterFormComponent onFiltersChanged={setFilterOptions} />
      </SearchFilterContainer>
      {filters && <RolesResults {...{ ...props, filters }} />}
    </SearchPageWrapper>
  );
};

export const RolesResults: React.FC<
  RolesPageProps & { filters: FilterOptions }
> = (props) => {
  useBreadcrumbs([{ text: "Employee Roles" }], []);

  const { filters } = props;

  const initialState = {
    tag: "Loading",
    items: [],
    showVisibility: false,
    loading: true,
    filterOptions: filters,
    searchToken: null,
    selectIdKey: "roleName",
    selectedItems: [],
  } as State<RoleAssignment, FilterOptions>;

  const { state, dispatch } = useInfiniteScroll<RoleAssignment, FilterOptions>(
    initialState
  );

  useEffect(() => {
    dispatch({
      tag: "SearchCriteriaChanged",
      filterOptions: filters,
    });
  }, [filters]);

  const reset = () => {
    dispatch({
      tag: "Reset",
      filterOptions: state.filterOptions,
    });
  };

  return (
    <ResetSearchItemsProvider reset={reset}>
      <TextHighlighterProvider searchText={state.filterOptions.searchText}>
        {state.tag === "Loading" && (
          <LoadRoles
            {...{
              dispatch,
              state: { ...state, searchToken: null },
              tag: "DataLoaded",
            }}
          />
        )}
        {state.tag === "LoadingMore" && (
          <LoadRoles
            {...{
              dispatch,
              tag: "MoreDataLoaded",
              state,
            }}
          />
        )}
        <RoleSearchPageDisplay
          {...{
            roles: state.items,
            showSpinner: state.loading,
            state,
            dispatch,
          }}
        />
      </TextHighlighterProvider>
    </ResetSearchItemsProvider>
  );
};
